import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User } from '../components/icons';
import { getBlogImage, blogImageMap } from '../assets/Blog';
import { API_BASE_URL } from '../lib/api';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  publishedAt: string;
  category: string;
  featuredImage?: string;
  slug: string;
  content: string;
}

const BlogSection: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/blog-working/blog?page=1&limit=4&status=PUBLISHED`);
        if (response.ok) {
          const data = await response.json();
          // If API returns empty posts, use mock data
          if (data.posts && data.posts.length > 0) {
            setBlogPosts(data.posts);
          } else {
            setBlogPosts(getMockBlogPosts());
          }
        } else {
          setBlogPosts(getMockBlogPosts());
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setBlogPosts(getMockBlogPosts());
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  const getMockBlogPosts = (): BlogPost[] => [
    {
      id: '1',
      title: "The Future of Dental Technology: Innovations Shaping Modern Dentistry",
      excerpt: "Explore the latest technological advancements in dental care and how they're revolutionizing patient treatment and practice management.",
      author: { 
        id: '1',
        firstName: "Dr. Sarah", 
        lastName: "Johnson",
        email: "sarah.johnson@dentopharma.com"
      },
      publishedAt: "2024-03-15T00:00:00Z",
      category: "Technology",
      slug: "future-dental-technology",
      content: "The Future of Dental Technology: Innovations Shaping Modern Dentistry. Explore the latest technological advancements in dental care and how they're revolutionizing patient treatment and practice management. This comprehensive guide covers everything from digital dentistry to AI-powered diagnostics.",
      featuredImage: "/images/blog/The Future of Dental Technology/The Future of Dental Technology.jpg"
    },
    {
      id: '2',
      title: "Essential Dental Supplies Every Practice Needs in 2024",
      excerpt: "A comprehensive guide to the must-have dental supplies and equipment that every modern dental practice should have in their inventory.",
      author: { 
        id: '2',
        firstName: "Dr. Michael", 
        lastName: "Chen",
        email: "michael.chen@dentopharma.com"
      },
      publishedAt: "2024-03-10T00:00:00Z",
      category: "Practice Management",
      slug: "essential-dental-supplies-2024",
      content: "Essential Dental Supplies Every Practice Needs in 2024. A comprehensive guide to the must-have dental supplies and equipment that every modern dental practice should have in their inventory. Learn about the latest products and how to optimize your practice efficiency.",
      featuredImage: "/images/blog/Essential Dental Supplies Every/Essential Dental Supplies Every.jpg"
    },
    {
      id: '3',
      title: "Understanding Dental Materials: A Complete Guide for Professionals",
      excerpt: "Deep dive into different dental materials, their properties, applications, and how to choose the right materials for specific procedures.",
      author: { 
        id: '3',
        firstName: "Dr. Emily", 
        lastName: "Rodriguez",
        email: "emily.rodriguez@dentopharma.com"
      },
      publishedAt: "2024-03-05T00:00:00Z",
      category: "Education",
      slug: "understanding-dental-materials",
      content: "Understanding Dental Materials: A Complete Guide for Professionals. Deep dive into different dental materials, their properties, applications, and how to choose the right materials for specific procedures. This educational resource helps professionals make informed decisions.",
      featuredImage: "/images/blog/Understanding Dental Materials/Understanding Dental Materials.jpg"
    },
    {
      id: '4',
      title: "Patient Safety in Dental Practice: Best Practices and Protocols",
      excerpt: "Learn about the essential safety protocols and best practices that ensure optimal patient care and compliance with industry standards.",
      author: { 
        id: '4',
        firstName: "Dr. James", 
        lastName: "Wilson",
        email: "james.wilson@dentopharma.com"
      },
      publishedAt: "2024-02-28T00:00:00Z",
      category: "Safety",
      slug: "patient-safety-dental-practice",
      content: "Patient Safety in Dental Practice: Best Practices and Protocols. Learn about the essential safety protocols and best practices that ensure optimal patient care and compliance with industry standards. This comprehensive guide covers infection control, emergency procedures, and quality assurance.",
      featuredImage: "/images/blog/Patient Safety in Dental Practice/Patient Safety in Dental Practice.jpg"
    }
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(' ').length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  const getBlogImageUrl = (post: BlogPost) => {
    // First, try to use the featuredImage from API if it exists
    if (post.featuredImage) {
      // Convert API image path to proper URL
      if (post.featuredImage.startsWith('/images/blog/')) {
        const filename = post.featuredImage.split('/').pop();
        if (filename) {
          const imageKey = filename.replace('.jpg', '').replace(/\s+/g, '-').toLowerCase();
          for (const [key, value] of Object.entries(blogImageMap)) {
            if (key.toLowerCase().includes(imageKey) || imageKey.includes(key.toLowerCase())) {
              return getBlogImage(value);
            }
          }
        }
      }
      // If it's already a proper URL, use it directly
      return post.featuredImage;
    }
    
    // Fallback to slug-based mapping
    const slugKey = blogImageMap[post.slug];
    if (slugKey) {
      return getBlogImage(slugKey);
    }
    
    // Final fallback
    return getBlogImage('default');
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Latest from Our Blog
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay updated with the latest insights, tips, and trends in dental care and practice management.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D56A6]"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Latest from Our Blog
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay updated with the latest insights, tips, and trends in dental care and practice management.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {blogPosts.map((post) => (
            <article
              key={post.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={getBlogImageUrl(post)}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = getBlogImage('default');
                  }}
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#0D56A6] text-white text-xs font-bold px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center text-sm text-gray-500 mb-3">
                  <User size={16} className="mr-2" />
                  <span className="mr-4">{post.author.firstName} {post.author.lastName}</span>
                  <Calendar size={16} className="mr-2" />
                  <span>{formatDate(post.publishedAt)}</span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#0D56A6] transition-colors duration-300">
                  {post.title}
                </h3>

                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{getReadTime(post.content)}</span>
                  <Link
                    to={`/blog/${post.id}`}
                    className="inline-flex items-center text-[#0D56A6] hover:text-[#0A4A8F] font-semibold text-sm group-hover:translate-x-1 transition-all duration-300"
                  >
                    Read More
                    <ArrowRight size={16} className="ml-1" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/blog"
            className="inline-flex items-center justify-center bg-[#0D56A6] hover:bg-[#0A4A8F] text-white font-bold text-lg px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            View All Articles
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
